import { zodResolver } from "@hookform/resolvers/zod";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { InputRadio } from "@itandi/itandi-bb-ui/experimental/InputRadio";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Form } from "@itandi/itandi-bb-ui/Form";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { InputDatev2 } from "@itandi/itandi-bb-ui/InputDatev2";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { RangeRow } from "@itandi/itandi-bb-ui/RangeRow";
import { formatDate } from "@itandi/tools/vanilla";
import type React from "react";
import { useId } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import { dealCacheKey } from "~/hooks/useDeals";
import { dealCacheKey as vendorDealCacheKey } from "~/hooks/vendors/useDeals";
import type { Deal } from "~/models/Deal";
import type {
	DealConstructionScheduleEdit,
	ScheduleInputFormValues,
	ScheduleStatus
} from "~/models/DealConstructionSchedule";
import {
	buildInitialSchedules,
	CONSTRUCTION_SCHEDULE_LABELS,
	calculateConstructionEndDate,
	scheduleInputFormSchema
} from "~/models/DealConstructionSchedule";
import { isOwnStatus } from "~/models/Status";
import { isSuccess } from "~/utils/api/api_handler";
import { update as updateDealConstructionSchedule } from "~/utils/api/deals/DealConstructionSchedule";
import { create as createVendorDealConstructionSchedule } from "~/utils/api/vendors/DealConstructionSchedule";
import { formatHyphenDate, toDate } from "~/utils/DateUtil";

type Props = {
	deal: Deal;
	handleClose: () => void;
};

const STATUS_OPTIONS = [
	{ label: "日程を入力", value: "scheduled" },
	{ label: "調整中", value: "adjusting" },
	{ label: "施工なし", value: "none" }
] as const satisfies { label: string; value: ScheduleStatus }[];

export const ScheduleInputModal: React.FC<Props> = ({
	deal,
	handleClose
}: Props) => {
	const formId = useId();
	const isOwnManagement = isOwnStatus(deal.statusId);

	const methods = useForm<ScheduleInputFormValues>({
		defaultValues: {
			schedules: buildInitialSchedules(deal)
		},
		mode: "onSubmit",
		resolver: zodResolver(scheduleInputFormSchema)
	}) as UseFormReturn<ScheduleInputFormValues>;
	const { fields } = useFieldArray({
		control: methods.control,
		name: "schedules"
	});
	const schedules = useWatch({
		control: methods.control,
		name: "schedules"
	});

	const constructionEndDate = calculateConstructionEndDate(schedules);
	const constructionEndDateValue = formatHyphenDate(constructionEndDate);
	const previousConstructionEndDateValue = formatHyphenDate(
		deal.constructionEndDate
	);
	const preferredDateValue = formatHyphenDate(deal.preferredDate);
	const preferredDateLabel =
		preferredDateValue === null ? null : formatDate(deal.preferredDate);

	const { trigger, isMutating } = useSWRMutation(
		isOwnManagement ? dealCacheKey(deal.id) : vendorDealCacheKey(deal.id),
		async (_key: string, { arg }: { arg: DealConstructionScheduleEdit[] }) =>
			isOwnManagement
				? updateDealConstructionSchedule(deal.id, arg)
				: createVendorDealConstructionSchedule(deal.id, arg)
	);

	const onSubmitSchedule = methods.handleSubmit(async (data) => {
		const schedulesToSubmit = data.schedules.map((schedule) => ({
			...schedule,
			endDate:
				schedule.constructionScheduleState === "scheduled"
					? schedule.endDate
					: null,
			startDate:
				schedule.constructionScheduleState === "scheduled"
					? schedule.startDate
					: null
		}));
		const result = await trigger(schedulesToSubmit);
		if (isSuccess(result)) {
			handleClose();
			toast.success("登録しました。");
		} else {
			toast.error(result.message ?? "エラーが発生しました。");
		}
	});

	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="工事日程の登録" />
			<ModalBody>
				<Paragraph mb={designTokens["spacing/m"]}>
					各工事の状況を選択してください。すべての工事が「日程入力済み」または「施工なしを選択した状態」になると、システム上の完工予定日が確定します。
				</Paragraph>
				<Form id={formId} onSubmit={onSubmitSchedule}>
					<FormGroup variant="vertical">
						{fields.map((field, index) => {
							const schedule = schedules[index];
							const startDate = toDate(schedule.startDate);
							const endDate = toDate(schedule.endDate);
							const errorMessage =
								methods.formState.errors.schedules?.[index]?.startDate
									?.message ??
								methods.formState.errors.schedules?.[index]?.endDate?.message;

							return (
								<FormControl
									isInvalid={!!errorMessage}
									key={field.constructionScheduleType}
									label={
										CONSTRUCTION_SCHEDULE_LABELS[
											schedule.constructionScheduleType
										]
									}
								>
									<Grid gap={designTokens["spacing/s"]}>
										<Flex alignItems="center" gap={designTokens["spacing/s"]}>
											{STATUS_OPTIONS.map((option) => (
												<InputRadio
													checked={
														schedule.constructionScheduleState === option.value
													}
													key={option.value}
													label={option.label}
													onChange={() => {
														methods.setValue(
															`schedules.${index}.constructionScheduleState`,
															option.value
														);
														if (option.value !== "scheduled") {
															methods.clearErrors([
																`schedules.${index}.startDate`,
																`schedules.${index}.endDate`
															]);
														}
													}}
													value={option.value}
												/>
											))}
										</Flex>
										{schedule.constructionScheduleState === "scheduled" && (
											<RangeRow
												from={
													<InputDatev2
														maxDate={endDate ?? undefined}
														onChange={(date) => {
															methods.setValue(
																`schedules.${index}.startDate`,
																date ? formatHyphenDate(date) : null
															);
														}}
														value={startDate}
													/>
												}
												to={
													<InputDatev2
														minDate={startDate ?? undefined}
														onChange={(date) => {
															methods.setValue(
																`schedules.${index}.endDate`,
																date ? formatHyphenDate(date) : null
															);
														}}
														value={endDate}
													/>
												}
											/>
										)}
										<FormControlText error={errorMessage} />
									</Grid>
								</FormControl>
							);
						})}
						<FormControl label="完工予定日" variant="vertical">
							<Label
								color={constructionEndDate !== null ? undefined : "subtle"}
							>
								{formatDate(constructionEndDate, "未定")}
							</Label>
						</FormControl>
					</FormGroup>
				</Form>
				{!isOwnManagement &&
					deal.constructionEndDate !== null &&
					constructionEndDateValue !== null &&
					previousConstructionEndDateValue !== null &&
					constructionEndDateValue > previousConstructionEndDateValue && (
						<Alertv2 mt={designTokens["spacing/m"]} variant="warning">
							<Paragraph>
								前回設定した完工予定日（
								{formatDate(deal.constructionEndDate)}
								）より遅い日程です。
								<br />
								トラブルを防ぐために、必ずチャット等で管理会社に連絡してください。
							</Paragraph>
						</Alertv2>
					)}
				{preferredDateValue !== null &&
					preferredDateLabel !== null &&
					constructionEndDateValue !== null &&
					constructionEndDateValue >= preferredDateValue && (
						<Alertv2 mt={designTokens["spacing/m"]} variant="warning">
							<Paragraph>
								入居予定日（{preferredDateLabel}
								）と同じか、それより遅い日程です。確認してください。
							</Paragraph>
						</Alertv2>
					)}
			</ModalBody>
			<ModalFooter
				leftArea={
					<Buttonv2
						disabled={isMutating}
						onClick={handleClose}
						variant="secondary"
					>
						キャンセル
					</Buttonv2>
				}
				rightArea={
					<Buttonv2
						form={formId}
						loading={isMutating}
						type="submit"
						variant="primary"
					>
						登録
					</Buttonv2>
				}
			/>
		</Modalv2>
	);
};
